import type { ReactNode } from "react";
import CheckIcon from "../../assets/svg/check-icon";

export interface CheckboxChipOptionProps {
  checked: boolean;
  label: ReactNode;
  onChange?: () => void;
  readOnly?: boolean;
  variant?: "chip" | "card";
  outerClassName?: string;
}

export function CheckboxChipOption({
  checked,
  label,
  onChange,
  readOnly = false,
  variant = "chip",
  outerClassName = "",
}: CheckboxChipOptionProps) {
  const padding = variant === "card" ? "p-3 gap-1.5" : "px-3 py-2.5 gap-1.5";

  const layoutClass =
    variant === "card" ? "flex w-full min-w-0" : "inline-flex max-w-full";

  const outer = `relative ${padding} ${layoutClass} items-center rounded-[8px] border transition-colors ${outerClassName} ${
    checked
      ? "border-selected-option-highlight dark:border-selected-option-highlight-dk bg-card-highlight-2 dark:bg-card-highlight-2-dk"
      : `border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk ${
          readOnly
            ? ""
            : "cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
        }`
  } ${readOnly ? "pointer-events-none" : "cursor-pointer"}`;

  const box = (
    <span
      className={`flex h-[14px] w-[14px] shrink-0 items-center justify-center transition-colors ${
        checked
          ? "bg-verba-blue dark:bg-verba-blue-dk rounded-[4px]"
          : "border border-gray-placeholder dark:border-gray-placeholder-dk bg-pure-white dark:bg-pure-white-dk rounded-[2px]"
      }`}
      aria-hidden
    >
      {checked ? (
        <CheckIcon className="w-2 h-2 text-pure-white dark:text-pure-white-dk" />
      ) : null}
    </span>
  );

  const textClass =
    "text-xs font-medium leading-[1.5] text-heading-text dark:text-heading-text-dk truncate";

  const inner = (
    <>
      {box}
      <span className={textClass}>{label}</span>
    </>
  );

  if (readOnly) {
    return (
      <div className={outer} aria-hidden>
        {inner}
      </div>
    );
  }

  return (
    <label className={outer}>
      <input
        type="checkbox"
        className="sr-only shrink-0 overflow-hidden whitespace-nowrap"
        checked={checked}
        onChange={onChange}
      />
      {inner}
    </label>
  );
}
